import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Owner-controlled three-photo facility gallery.
 * Always starts on PHOTO 1 (the primary/cover image), always follows the fixed
 * sequence PHOTO 1 -> PHOTO 2 -> PHOTO 3 defined in lib/site.js, and never autoplays.
 */
export default function FacilityGallery({ facility }) {
  const photos = facility.photos;
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(photos.length - 1, i + 1));

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx <= -40) next();
    if (dx >= 40) prev();
  };

  return (
    <div
      role="group"
      aria-roledescription="gallery"
      aria-label={`${facility.name} photo gallery`}
      data-testid={`facility-gallery-${facility.id}`}
      className="relative overflow-hidden bg-ivory"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="flex transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {photos.map((photo) => (
          <img
            key={photo.slot}
            src={photo.src}
            alt={photo.alt}
            loading="lazy"
            draggable="false"
            className="aspect-[4/3] w-full shrink-0 select-none object-cover"
          />
        ))}
      </div>

      <p
        data-testid={`gallery-counter-${facility.id}`}
        aria-live="polite"
        className="absolute right-3 top-3 rounded-full bg-forest/75 px-2.5 py-1 text-[0.7rem] font-medium tracking-wide text-ivory"
      >
        {index + 1} / {photos.length}
      </p>

      <button
        type="button"
        data-testid={`gallery-prev-${facility.id}`}
        onClick={prev}
        disabled={index === 0}
        aria-label={`Previous photo at ${facility.name}`}
        className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-inkline bg-ivory/90 text-forest shadow-sm transition-opacity duration-300 hover:bg-ivory disabled:opacity-30"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        data-testid={`gallery-next-${facility.id}`}
        onClick={next}
        disabled={index === photos.length - 1}
        aria-label={`Next photo at ${facility.name}`}
        className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-inkline bg-ivory/90 text-forest shadow-sm transition-opacity duration-300 hover:bg-ivory disabled:opacity-30"
      >
        <ChevronRight className="h-5 w-5" aria-hidden="true" />
      </button>

      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
        {photos.map((photo, i) => (
          <button
            key={photo.slot}
            type="button"
            data-testid={`gallery-dot-${facility.id}-${photo.slot}`}
            onClick={() => setIndex(i)}
            aria-label={`Show photo ${photo.slot} of ${photos.length}`}
            aria-current={i === index ? "true" : undefined}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index ? "w-5 bg-clay" : "w-1.5 bg-ivory/80"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export function GalleryPending({ facilityId }) {
  return (
    <div
      data-testid={`gallery-pending-${facilityId}`}
      className="flex aspect-[4/3] w-full items-center justify-center bg-sage-mist/40"
    >
      <p className="px-6 text-center text-[0.9rem] text-forest/60">
        Facility photography to be added
      </p>
    </div>
  );
}
